import {ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild} from "@angular/core";
import {ScenarioType} from "../../../../shared/model/scenario-type";
import {filter, map, Observable, Subscription, take} from "rxjs";
import {Store} from "@ngrx/store";
import {AggregatedDealCashflows} from "../../../models/aggregated-deal-cashflows";
import {selectSelectedDeal, selectSelectedDealCashFlowsForScenarioType, selectWeightedCashflowBreakdown} from "../../../store/deal/deal.selectors";
import {selectDealSensitivityDetailsCashflowForScnType} from "../../../deal-sensitivity-page/store/deal-sensitivity.selectors";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {AggregatedDealCashflowRecord} from "../../../models/aggregated-deal-cashflow-record";
import {DateUtil} from "../../../../shared/utils/date-util";
import {Router} from "@angular/router";
import {DealWeightedCashflowBreakdownActions} from "../../../store/deal.actions";
import {DecimalFormatPipe} from "../../../../shared/pipes/decimal-format/decimal-format.pipe";
import Big from "big.js";

@Component({
    selector: "valumize-deal-cashflow-table",
    templateUrl: "./deal-cashflow-table.component.html",
    styleUrls: ["./deal-cashflow-table.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DealCashflowTableComponent implements OnInit, OnChanges, OnDestroy {

    @ViewChild("popupTemplate") popupTemplate!: TemplateRef<any>;

    @Input() dealId?: number;
    @Input() scenarioType: ScenarioType = "BASE";
    @Input() displaySecondaryColumns = false;
    @Input() displayPatternColumns = false;
    @Input() displayHurdleColumns = false;
    @Input() isBottomUpValuation = false;
    @Input() isSensitivityView = false;
    dialogRef!: MatDialogRef<any>;
    subscriptions: Subscription[] = [];
    selectCashflowByScenarioType$?: Observable<{ aggregatedDealCashFlows: AggregatedDealCashflows | undefined }>;
    currency$?: Observable<string | undefined>;
    allColumns = [
        "date",
        "dealAgeInQuarters",
        "assetDistribution",
        "assetDrawdown",
        "gpManagementFee",
        "gpUnfundedPayment",
        "gpUnfundedDistribution",
        "gpUnfundedNet",
        "purchasePrice",
        "gpGross",
        "gpCarryPayment",
        "secondaryCashflow",
        "secondaryManagementFee",
        "secondaryManagementFeePattern",
        "secondaryCarryNet",
        "secondaryCarryPayment",
        "secondaryCarryPaymentHurdle",
        "secondaryNetCashflow"
    ];
    displayColumns = this.allColumns;

    readonly DIALOG_COLUMNS = ["low", "base", "high", "weighting", "weighted"];
    readonly DIALOG_COLUMN_LABELS: Record<string, string> = {
        low: "LOW",
        base: "BASE",
        high: "HIGH",
        weighting: "Weighting (L/B/H)",
        weighted: "Weighted"
    };
    /** Weighting (L/B/H) is available in the picker but hidden by default; every other column shows by default. */
    readonly DEFAULT_SELECTED_COLUMNS = this.DIALOG_COLUMNS.filter(col => col !== "weighting");
    showUnderlyingAssets = true;
    selectedColumns = [...this.DEFAULT_SELECTED_COLUMNS];

    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly dialog = inject(MatDialog);

    ngOnInit(): void {
        this.setDisplayedColumns();

        this.subscriptions.push(
            this.router.events.subscribe(() => {
                if (this.dialogRef) {
                    this.dialogRef.close();
                }
            })
        );
        this.selectCashflowByScenarioType$ = this.isSensitivityView
            ? this.store.select(selectDealSensitivityDetailsCashflowForScnType(this.scenarioType))
            : this.store.select(selectSelectedDealCashFlowsForScenarioType(this.scenarioType));
        this.currency$ = this.store.select(selectSelectedDeal).pipe(
            map(deal => deal?.currencyIso?.code ?? undefined)
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (
            (changes["displaySecondaryColumns"] && !changes["displaySecondaryColumns"].firstChange) ||
            (changes["displayPatternColumns"] && !changes["displayPatternColumns"].firstChange) ||
            (changes["displayHurdleColumns"] && !changes["displayHurdleColumns"].firstChange)
        ) {
            this.setDisplayedColumns();
        }
    }

    setDisplayedColumns() {
        const hide = (c: string) =>
            (!this.displaySecondaryColumns && c.startsWith("secondary") && c !== "secondaryCashflow") ||
            (!this.displayPatternColumns && c.endsWith("Pattern")) ||
            (!this.displayHurdleColumns && c.endsWith("Hurdle"));

        this.displayColumns = this.allColumns.filter(c => !hide(c));
    }

    openPopup(record: AggregatedDealCashflowRecord, cashflowTypes: string[]): void {
        if (!this.dealId) return;
        const cashflowDate = DateUtil.fromIsoDate(record.date);
        const titlePrefix = cashflowTypes.includes("EXIT") || cashflowTypes.includes("DISTRIBUTION")
            ? "Asset Cashflow" : "Asset Drawdown";
        const date = cashflowDate?.toFormat("dd/MM/yyyy");

        this.store.dispatch(DealWeightedCashflowBreakdownActions.load({
            dealId: this.dealId,
            date: record.date,
            cashflowTypes
        }));

        this.subscriptions.push(
            this.store.select(selectWeightedCashflowBreakdown).pipe(
                filter(s => s.status === "LOADED" || s.status === "ERROR"),
                take(1)
            ).subscribe(state => {
                if (state.status === "ERROR" || !state.data || state.data.fundBreakdowns.length === 0) return;
                const {fundBreakdowns: funds, lowTotal, baseTotal, highTotal, weightedTotal, lowWeight, baseWeight, highWeight} = state.data;
                const totals = {low: lowTotal, base: baseTotal, high: highTotal, weighted: weightedTotal};
                const dealWeight = {low: lowWeight, base: baseWeight, high: highWeight};
                this.dialogRef = this.dialog.open(this.popupTemplate, {
                    width: "900px",
                    maxWidth: "95vw",
                    data: {funds, totals, dealWeight, titlePrefix, date, scenarioType: this.scenarioType},
                    autoFocus: false
                });
                this.subscriptions.push(
                    this.dialogRef.afterClosed().subscribe(() => {
                        this.showUnderlyingAssets = true;
                        this.selectedColumns = [...this.DEFAULT_SELECTED_COLUMNS];
                    })
                );
            })
        );
    }

    navigateToFund(fund: { fundId?: number; fundReportId?: number; fundValuationId?: number }): void {
        if (!fund.fundId) {
            return;
        }
        this.router.navigate(["../../funds/", fund.fundId], {
            queryParams: {
                dealId: this.dealId,
                fundReportId: fund.fundReportId,
                fundValuationId: fund.fundValuationId
            }
        });
        this.dialogRef?.close();
    }

    onColumnPickerChange(cols: string[]): void {
        this.selectedColumns = cols;
    }

    /**
     * Formats a Low/Base/High weighting triple (fractions) as whole percents "L / B / H", or "various"
     * when the triple is not shared across the aggregated line's contributors (any component missing).
     * Weightings written today are whole percentages (WI 1085), but legacy scenarios predate that
     * constraint and may hold free-form fractions, so round to the nearest whole percent for display.
     */
    formatWeight(low?: number, base?: number, high?: number): string {
        if (low == null || base == null || high == null) {
            return "various";
        }
        const pct = (fraction: number) => new Big(DecimalFormatPipe.transformFractionToPercent(fraction) ?? 0).round(0).toString();
        return `${pct(low)} / ${pct(base)} / ${pct(high)}`;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
